/* src/modules/office/OfficeScheduler.js */
import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { CalendarIcon, UserGroupIcon, ChartBarIcon, CheckCircleIcon } from '../../components/icons/Icons'; // ✅ Import CheckCircleIcon

import MyOfficeCalendar from './components/MyOfficeCalendar';
import TeamScheduleManager from './components/TeamScheduleManager';
import OfficeAttendanceReport from './components/OfficeAttendanceReport';
import DeviationApprovalPanel from './components/DeviationApprovalPanel'; // ✅ Import New Component

export default function OfficeScheduler({ userProfile, appId }) {
    const [activeTab, setActiveTab] = useState('calendar');
    const [isManager, setIsManager] = useState(false);
    
    // Check if HR or Admin
    const isHRAdmin = userProfile.role === 'Admin' || userProfile.department === 'HR';

    useEffect(() => {
        const checkManager = async () => {
            if (userProfile.role === 'Admin') {
                setIsManager(true);
                return;
            }
            const q = query(collection(db, `artifacts/${appId}/users`), where("managerId", "==", userProfile.id));
            const snap = await getDocs(q);
            setIsManager(!snap.empty);
        };
        checkManager();
    }, [appId, userProfile]);

    // Combined permission check for management tabs
    const canManage = isManager || isHRAdmin;

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-[#213368]">Office Scheduler</h2>
                    <p className="text-sm text-gray-500">Plan your hybrid work week and manage deviations.</p>
                </div>
            </div>

            <div className="flex space-x-1 border-b border-gray-200 overflow-x-auto">
                <TabButton id="calendar" label="My Calendar" activeTab={activeTab} setActiveTab={setActiveTab} />
                
                {/* Team Schedule: Managers Only */}
                {canManage && <TabButton id="team_plan" label="Team Schedule" activeTab={activeTab} setActiveTab={setActiveTab} />}
                
                {/* Requests: Managers Only (NEW) */}
                {canManage && <TabButton id="requests" label="Requests" activeTab={activeTab} setActiveTab={setActiveTab} />}
                
                <TabButton id="reports" label="Attendance Reports" activeTab={activeTab} setActiveTab={setActiveTab} />
            </div>

            <div className="bg-white p-6 rounded-b-lg rounded-tr-lg shadow-sm border border-gray-200 min-h-[500px]">
                {activeTab === 'calendar' && <MyOfficeCalendar userProfile={userProfile} appId={appId} />}
                {activeTab === 'team_plan' && canManage && <TeamScheduleManager userProfile={userProfile} appId={appId} />}
                
                {/* ✅ Render the new panel */}
                {activeTab === 'requests' && canManage && <DeviationApprovalPanel userProfile={userProfile} appId={appId} />}
                
                {activeTab === 'reports' && <OfficeAttendanceReport userProfile={userProfile} appId={appId} isManager={canManage} />}
            </div>
        </div>
    );
}

function TabButton({ id, label, activeTab, setActiveTab }) {
    return (
        <button
            onClick={() => setActiveTab(id)}
            className={`flex items-center px-6 py-3 text-sm font-medium transition-colors border-b-2 rounded-t-lg whitespace-nowrap ${
                activeTab === id 
                    ? 'border-[#20BEC6] text-[#20BEC6] bg-blue-50' 
                    : 'border-transparent text-gray-500 hover:text-[#213368] hover:bg-gray-50'
            }`}
        >
            {label}
        </button>
    );
}